/**
 * What screen you were on when your phone reloaded the page behind your back.
 *
 * Reading an emailed code means leaving the browser. A backgrounded tab on a
 * phone is a tab the operating system may discard, and coming back to it
 * re-runs the application from its first line: `pendingEmail` is null again,
 * the player's phase is `join` again, and someone holding a perfectly good code
 * is looking at a form asking for their email address. Every bit of the sign-in
 * is working; it just forgot, at the one moment the whole flow depends on it
 * not forgetting.
 *
 * So the one fact that cannot be re-derived — which address a code was just
 * sent to, and what it was for — is written down before the trip and read back
 * on the way in.
 *
 * `localStorage` rather than `sessionStorage`, because the tab identity that
 * scopes a session store is exactly the thing an eviction can take with it, and
 * mail apps sometimes hand the return trip to a different tab entirely. The
 * cost of the broader store is a stale record, which the expiry below answers.
 *
 * Nothing secret is kept here. The code itself is never written down — this is
 * an address and a room, on a device that had both a moment ago.
 */

const STORAGE_KEY = "tq.pendingCode";

/**
 * How long a written record is worth restoring, in milliseconds.
 *
 * The server's `OTP_POLICY.expirySeconds`, which this cannot import — that
 * module pulls Better Auth in with it, and this one runs on a phone. Kept
 * deliberately equal: restoring the code screen for a code that has expired
 * puts someone in front of six boxes that cannot work, which is worse than the
 * form they would otherwise have seen. When the server's window moves, move
 * this with it.
 */
const WINDOW_MS = 15 * 60 * 1000;

export interface PendingCodeSession {
  /** The room code, so verification can continue into onboarding. */
  code: string;
  title: string;
  /**
   * Whether the code screen offers to ask the facilitator instead. Kept so a
   * reload does not quietly take the offer away from someone whose code is
   * not coming.
   */
  accessRequests?: boolean;
}

export interface PendingCode {
  email: string;
  /** Epoch milliseconds. Past this, the code it refers to is dead anyway. */
  expiresAt: number;
  /** Present for a player mid-join; absent for the console, which has no event. */
  session?: PendingCodeSession;
}

/**
 * The record, if there is a live one.
 *
 * Anything unreadable, misshapen or expired is treated as absent AND cleared,
 * so one bad write cannot keep re-deciding what screen someone sees.
 */
export function readPendingCode(): PendingCode | null {
  let raw: string | null;
  try {
    raw = localStorage.getItem(STORAGE_KEY);
  } catch {
    // Private browsing, or storage disabled. Sign-in still works; it just will
    // not survive a reload, which is where this started.
    return null;
  }
  if (!raw) return null;

  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch {
    clearPendingCode();
    return null;
  }

  const record = parsed as Partial<PendingCode> | null;
  if (!record || typeof record.email !== "string" || typeof record.expiresAt !== "number") {
    clearPendingCode();
    return null;
  }
  if (record.expiresAt <= Date.now()) {
    clearPendingCode();
    return null;
  }

  const session = record.session;
  const restored: PendingCode = { email: record.email, expiresAt: record.expiresAt };
  if (session && typeof session.code === "string" && typeof session.title === "string") {
    restored.session = { code: session.code, title: session.title };
    if (session.accessRequests === true) restored.session.accessRequests = true;
  }
  return restored;
}

/** Called the moment a code is on its way — before anyone leaves for it. */
export function writePendingCode(email: string, session?: PendingCodeSession): void {
  const record: PendingCode = { email, expiresAt: Date.now() + WINDOW_MS };
  if (session) record.session = session;
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(record));
  } catch {
    // Non-fatal: the sign-in in front of the user is unaffected.
  }
}

/**
 * Called on every exit from the code screen — verified, abandoned, or a
 * different address. A record outliving its screen is what would put a stale
 * code screen in front of the next person to open the console on this laptop.
 */
export function clearPendingCode(): void {
  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch {
    /* ignored */
  }
}
